import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum'

type DatadogBeforeSend = NonNullable<RumInitConfiguration['beforeSend']>

type DatadogIgnoreNetworkError = Partial<{
  url: string
  method: string
  status: number
  code: string
}>

const DEFAULT_DATADOG_IGNORE_ERROR_MESSAGES = [
  /Non-Error exception captured/,
  /Network Error/,
  /Authentication failed/,
  /Request failed with status code 401/,
  /USER_WRONG_PASSWORD/,
  /USER_NOT_FOUND/,
  /Request aborted/,
]

const isElectronEnvironment = () => {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron')
}

export const DATADOG_ERROR_TRACKING_ONLY_CONFIG = (datadogClient: DatadogClient) =>
  ({
    site: 'datadoghq.com',
    trackUserInteractions: false,
    trackResources: false,
    trackLongTasks: false,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: ((event, context) => {
      if (datadogClient.debug === 'all') {
        console.log(`datadog event: ${event.type}`, event)
      }

      // 비용이 큰 RUM 부가 이벤트는 SDK 설정과 beforeSend 양쪽에서 차단합니다.
      if (event.type === 'resource' || event.type === 'action') return false
      if (event.type === 'error') {
        if (datadogClient.debug === 'error') {
          console.log(`datadog error: ${event.error?.message}`)
          console.log(event)
          console.log(context)
        }

        const isIgnored = datadogClient.errorMessagesToIgnore.some((ignoreErrorMessage) => {
          return ignoreErrorMessage.test(event.error?.message ?? '')
        })

        if (isIgnored || event.error.type === 'CanceledError') return false
      }

      return true
    }) satisfies DatadogBeforeSend,
  }) satisfies Partial<RumInitConfiguration>

export class DatadogClient {
  enabled = false
  debug: 'all' | 'error' | false | string = false
  networkErrorsToIgnore: DatadogIgnoreNetworkError[] = []
  errorMessagesToIgnore: RegExp[] = DEFAULT_DATADOG_IGNORE_ERROR_MESSAGES
  private _config: RumInitConfiguration
  private static _instance = datadogRum

  constructor({
    enabled,
    debug,
    networkErrorsToIgnore,
    errorMessagesToIgnore,
    config: { beforeSend, ..._config },
  }: {
    enabled: boolean
    debug?: 'all' | 'error' | false | string
    networkErrorsToIgnore?: DatadogIgnoreNetworkError[]
    errorMessagesToIgnore?: RegExp[]
    config: RumInitConfiguration
  }) {
    // 호출자가 enabled=true를 넘기더라도 Electron에서는 Datadog 초기화와 후속 전송을 모두 막습니다.
    // initialize/sendError/setUser 계열 메서드는 this.enabled를 공통 게이트로 사용합니다.
    this.enabled = enabled && !isElectronEnvironment()
    this.debug = debug ?? false

    if (networkErrorsToIgnore) {
      this.networkErrorsToIgnore = [...this.networkErrorsToIgnore, ...networkErrorsToIgnore]
    }
    if (errorMessagesToIgnore) {
      this.errorMessagesToIgnore = [...this.errorMessagesToIgnore, ...errorMessagesToIgnore]
    }

    this._config = {
      ...DATADOG_ERROR_TRACKING_ONLY_CONFIG(this),
      ..._config,
    }
  }

  get instance() {
    return DatadogClient._instance
  }

  get initialized() {
    return this.instance.getInitConfiguration() !== undefined
  }

  initialize(config?: RumInitConfiguration) {
    if (!this.enabled) return
    DatadogClient._instance.init({
      ...this._config,
      ...config,
    })
    if (this.debug) {
      console.info('[Datadog] initialized')
    }
  }

  sendError(...args: Parameters<typeof datadogRum.addError>) {
    if (!this.enabled || !this.initialized) return
    return this.instance.addError(...args)
  }

  setUser(...args: Parameters<typeof datadogRum.setUser>) {
    if (!this.enabled || !this.initialized) return
    return this.instance.setUser(...args)
  }

  setAccount(...args: Parameters<typeof datadogRum.setAccount>) {
    if (!this.enabled || !this.initialized) return
    return this.instance.setAccount(...args)
  }

  setGlobalContextProperty(...args: Parameters<typeof datadogRum.setGlobalContextProperty>) {
    if (!this.enabled || !this.initialized) return
    return this.instance.setGlobalContextProperty(...args)
  }
}
